// src/pages/Private/Teacher/QuizManagement/CreateQuiz.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createQuiz, getLessonById } from '../../../../api/teacherApi';
import './CreateQuiz.css';

function CreateQuiz() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState({
    title: '',
    description: '',
    timeLimit: 30,
    passingScore: 70,
    attempts: 3,
    questions: []
  });
  const [currentQuestion, setCurrentQuestion] = useState({
    question: '',
    type: 'multiple',
    options: ['', ''],
    correctAnswer: '',
    points: 1
  });

  useEffect(() => {
    loadLesson();
  }, [lessonId]);

  const loadLesson = async () => {
    try {
      const data = await getLessonById(lessonId);
      setLesson(data);
    } catch (error) {
      console.error('Ders yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = () => {
    if (!currentQuestion.question || !currentQuestion.correctAnswer) {
      alert('Lütfen soru ve doğru cevabı girin');
      return;
    }

    // Çoktan seçmeli soru için seçenek kontrolü
    if (currentQuestion.type === 'multiple' && currentQuestion.options.some(opt => !opt)) {
      alert('Lütfen tüm seçenekleri doldurun');
      return;
    }

    setQuiz({
      ...quiz,
      questions: [...quiz.questions, currentQuestion]
    });

    setCurrentQuestion({
      question: '',
      type: 'multiple',
      options: ['', ''],
      correctAnswer: '',
      points: 1
    });
  };

  const removeQuestion = (index) => {
    setQuiz({
      ...quiz,
      questions: quiz.questions.filter((_, i) => i !== index)
    });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...currentQuestion.options];
    newOptions[index] = value;
    setCurrentQuestion({ ...currentQuestion, options: newOptions });
  };

  const addOption = () => {
    setCurrentQuestion({
      ...currentQuestion,
      options: [...currentQuestion.options, '']
    });
  };

  const removeOption = (index) => {
    if (currentQuestion.options.length <= 2) return;
    setCurrentQuestion({
      ...currentQuestion,
      options: currentQuestion.options.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (quiz.questions.length === 0) {
      alert('En az bir soru eklemelisiniz');
      return;
    }

    try {
      await createQuiz(lessonId, quiz);
      navigate(`/teacher/lesson/${lessonId}`);
    } catch (error) {
      console.error('Quiz oluşturulurken hata:', error);
    }
  };

  if (loading) return <div className="loading">Yükleniyor...</div>;

  return (
    <div className="create-quiz">
      <div className="page-header">
        <h1>Quiz Oluştur</h1>
        <p>Ders: {lesson?.title}</p>
      </div>

      <form onSubmit={handleSubmit} className="quiz-form">
        <div className="form-section">
          <h2>Quiz Bilgileri</h2>
          <div className="form-group">
            <label>Quiz Başlığı</label>
            <input
              type="text"
              value={quiz.title}
              onChange={(e) => setQuiz({...quiz, title: e.target.value})}
              required
              placeholder="Örn: React Component'ler Quiz"
            />
          </div>

          <div className="form-group">
            <label>Açıklama</label>
            <textarea
              value={quiz.description}
              onChange={(e) => setQuiz({...quiz, description: e.target.value})}
              rows="3"
              placeholder="Quiz hakkında kısa bir açıklama..."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Süre (dakika)</label>
              <input
                type="number"
                value={quiz.timeLimit}
                onChange={(e) => setQuiz({...quiz, timeLimit: parseInt(e.target.value)})}
                min="1"
                max="180"
              />
            </div>

            <div className="form-group">
              <label>Geçme Notu (%)</label>
              <input
                type="number"
                value={quiz.passingScore}
                onChange={(e) => setQuiz({...quiz, passingScore: parseInt(e.target.value)})}
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label>Maksimum Deneme</label>
              <input
                type="number"
                value={quiz.attempts}
                onChange={(e) => setQuiz({...quiz, attempts: parseInt(e.target.value)})}
                min="1"
                max="10"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Sorular ({quiz.questions.length})</h2>

          <div className="current-question">
            <h3>Yeni Soru Ekle</h3>
            
            <div className="form-group">
              <label>Soru Metni</label>
              <input
                type="text"
                value={currentQuestion.question}
                onChange={(e) => setCurrentQuestion({...currentQuestion, question: e.target.value})}
                placeholder="Soru metnini girin..."
              />
            </div>

            <div className="form-group">
              <label>Soru Tipi</label>
              <select
                value={currentQuestion.type}
                onChange={(e) => setCurrentQuestion({...currentQuestion, type: e.target.value})}
              >
                <option value="multiple">Çoktan Seçmeli</option>
                <option value="truefalse">Doğru/Yanlış</option>
              </select>
            </div>

            {currentQuestion.type === 'multiple' && (
              <div className="options-section">
                <label>Seçenekler</label>
                {currentQuestion.options.map((option, index) => (
                  <div key={index} className="option-input">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder={`Seçenek ${index + 1}`}
                    />
                    {currentQuestion.options.length > 2 && (
                      <button type="button" onClick={() => removeOption(index)} className="remove-option">
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addOption} className="add-option">
                  + Seçenek Ekle
                </button>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Doğru Cevap</label>
                {currentQuestion.type === 'truefalse' ? (
                  <select
                    value={currentQuestion.correctAnswer}
                    onChange={(e) => setCurrentQuestion({...currentQuestion, correctAnswer: e.target.value})}
                  >
                    <option value="">Seçiniz</option>
                    <option value="true">Doğru</option>
                    <option value="false">Yanlış</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    value={currentQuestion.correctAnswer}
                    onChange={(e) => setCurrentQuestion({...currentQuestion, correctAnswer: e.target.value})}
                    placeholder="Doğru cevabı yazın"
                  />
                )}
              </div>

              <div className="form-group">
                <label>Puan</label>
                <input
                  type="number"
                  value={currentQuestion.points}
                  onChange={(e) => setCurrentQuestion({...currentQuestion, points: parseInt(e.target.value)})}
                  min="1"
                  max="100"
                />
              </div>
            </div>

            <button type="button" onClick={addQuestion} className="add-question-btn">
              Soruyu Ekle
            </button>
          </div>

          {quiz.questions.length > 0 && (
            <div className="questions-list">
              <h3>Eklenen Sorular</h3>
              {quiz.questions.map((q, index) => (
                <div key={index} className="question-item">
                  <div className="question-header">
                    <strong>Soru {index + 1}:</strong> {q.question}
                    <button type="button" onClick={() => removeQuestion(index)} className="remove-question">
                      ✕
                    </button>
                  </div>
                  <div className="question-meta">
                    <span>Tip: {q.type === 'multiple' ? 'Çoktan Seçmeli' : 'Doğru/Yanlış'}</span>
                    <span>Puan: {q.points}</span>
                    <span>Doğru: {q.correctAnswer}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary">
            Quiz Oluştur
          </button>
          <button type="button" className="btn-cancel" onClick={() => navigate(-1)}>
            İptal
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateQuiz;